import React, { useState, useEffect, useRef } from 'react';
import { theme } from '../styles/theme';
import { 
  fetchContactConversations, 
  sendAdminMessage, 
  markMessagesAsRead,
  subscribeToMessages 
} from '../services/firebase';
import type { ContactConversation, Message } from '../types';
import { formatDayLabel, formatHourLabel, formatLastMessageDate } from '../utils/dateFormat';
import SendIcon from '@mui/icons-material/Send';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useMediaQuery from '@mui/material/useMediaQuery';

const Support: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [conversations, setConversations] = useState<ContactConversation[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Carregar lista de conversas
  useEffect(() => {
    loadConversations();
  }, []);

  // Subscrever para mensagens em tempo real quando uma conversa é selecionada
  useEffect(() => {
    if (!selectedUserId) {
      setMessages([]);
      return;
    }

    const unsubscribe = subscribeToMessages(selectedUserId, (newMessages) => {
      setMessages(newMessages);
      
      // Scroll automático após carregar mensagens
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    });

    // Marcar mensagens como lidas
    markMessagesAsRead(selectedUserId).catch(console.error);

    return () => {
      unsubscribe();
    };
  }, [selectedUserId]);

  // Listener de scroll para botão de scroll-down
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const offsetFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      setShowScrollButton(offsetFromBottom > 200);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedUserId]);

  useEffect(() => {
    if (!isMobile) {
      setShowMobileChat(false);
    }
  }, [isMobile]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await fetchContactConversations();
      setConversations(data);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
      alert('Erro ao carregar conversas');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = async (userId: string) => {
    setSelectedUserId(userId);
    if (isMobile) {
      setShowMobileChat(true);
    }
  };

  const handleBackToList = () => {
    setShowMobileChat(false);
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedUserId || sending) return;

    const text = messageInput.trim();
    try {
      setSending(true);
      const createdAt = await sendAdminMessage(selectedUserId, text);
      setMessageInput('');

      // A mensagem enviada já aparece no chat aberto via listener realtime.
      // Aqui só atualizamos a prévia da lista lateral localmente (sem refetch
      // de todos os usuários, que recarregava a tela inteira a cada envio).
      setConversations((prev) =>
        [...prev]
          .map((c) =>
            c.userId === selectedUserId
              ? { ...c, lastMessage: text, lastMessageDate: createdAt }
              : c
          )
          .sort((a, b) => {
            if (!a.lastMessageDate) return 1;
            if (!b.lastMessageDate) return -1;
            return new Date(b.lastMessageDate).getTime() - new Date(a.lastMessageDate).getTime();
          })
      );

      // Scroll para baixo após enviar
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const renderDayBadge = (date: string) => (
    <div style={styles.dayBadge}>
      <span style={styles.dayBadgeText}>{formatDayLabel(date)}</span>
    </div>
  );

  const renderMessageBubble = (message: Message, index: number) => {
    const isAdmin = !message.fromMe; // fromMe true = cliente, false = admin
    const showDayBadge = index === 0 || 
      new Date(messages[index - 1].createdAt).toDateString() !== new Date(message.createdAt).toDateString();

    return (
      <div key={message.id}>
        {showDayBadge && renderDayBadge(message.createdAt)}
        <div style={{
          ...styles.messageBubbleContainer,
          justifyContent: isAdmin ? 'flex-end' : 'flex-start',
        }}>
          <div style={{
            ...styles.messageBubble,
            ...(isAdmin ? styles.messageBubbleAdmin : styles.messageBubbleClient),
          }}>
            <div style={styles.messageText}>{message.msg}</div>
            <div style={{
              ...styles.messageTime,
              color: isAdmin ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            }}>
              {formatHourLabel(message.createdAt)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const selectedConversation = conversations.find(c => c.userId === selectedUserId);
  const showConversationsList = !isMobile || !showMobileChat;
  const showChatArea = !isMobile || showMobileChat;

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Carregando conversas...</div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, ...(isMobile ? styles.containerMobile : {}) }}>
      {/* Lista de conversas */}
      {showConversationsList && (
      <div style={{ ...styles.conversationsList, ...(isMobile ? styles.conversationsListMobile : {}) }}>
        <div style={styles.conversationsHeader}>
          <h2 style={styles.conversationsTitle}>Suporte</h2>
        </div>
        
        {conversations.length === 0 ? (
          <div style={styles.emptyState}>
            <PersonIcon style={{ fontSize: 48, color: '#999', marginBottom: 10 }} />
            <p style={styles.emptyStateText}>Nenhuma conversa ainda</p>
          </div>
        ) : (
          <div style={styles.conversationsScroll}>
            {conversations.map((conv) => (
              <div
                key={conv.userId}
                onClick={() => handleSelectConversation(conv.userId)}
                style={{
                  ...styles.conversationItem,
                  ...(selectedUserId === conv.userId ? styles.conversationItemActive : {}),
                }}
              >
                <div style={styles.conversationAvatar}>
                  {conv.photoUrl ? (
                    <img src={conv.photoUrl} alt={conv.businessName} style={styles.avatarImage} />
                  ) : (
                    <PersonIcon style={{ fontSize: 32, color: '#fff' }} />
                  )}
                </div>
                <div style={styles.conversationInfo}>
                  <div style={styles.conversationHeader}>
                    <span style={styles.conversationName}>{conv.businessName}</span>
                    <span style={styles.conversationDate}>
                      {formatLastMessageDate(conv.lastMessageDate)}
                    </span>
                  </div>
                  <div style={styles.conversationLastMessage}>
                    <span style={styles.lastMessageText}>
                      {conv.lastMessage.length > 40 
                        ? conv.lastMessage.substring(0, 40) + '...' 
                        : conv.lastMessage}
                    </span>
                    {conv.unreadCount > 0 && (
                      <span style={styles.unreadBadge}>{conv.unreadCount}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Área de chat */}
      {showChatArea && (
      <div style={{ ...styles.chatArea, ...(isMobile ? styles.chatAreaMobile : {}) }}>
        {!selectedUserId ? (
          <div style={styles.emptyChat}>
            <PersonIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 20 }} />
            <p style={styles.emptyChatText}>
              Selecione uma conversa para começar
            </p>
          </div>
        ) : (
          <>
            {/* Cabeçalho do chat */}
            <div style={styles.chatHeader}>
              <div style={styles.chatHeaderInfo}>
                {isMobile && (
                  <button
                    type="button"
                    onClick={handleBackToList}
                    style={styles.mobileBackButton}
                    title="Voltar"
                    aria-label="Voltar para conversas"
                  >
                    <ArrowBackIcon style={{ fontSize: 20 }} />
                  </button>
                )}
                <div style={styles.chatAvatar}>
                  {selectedConversation?.photoUrl ? (
                    <img 
                      src={selectedConversation.photoUrl} 
                      alt={selectedConversation.businessName} 
                      style={styles.avatarImage} 
                    />
                  ) : (
                    <PersonIcon style={{ fontSize: 28, color: '#fff' }} />
                  )}
                </div>
                <div>
                  <div style={styles.chatHeaderName}>
                    {selectedConversation?.businessName}
                  </div>
                  <div style={styles.chatHeaderEmail}>
                    {selectedConversation?.mail}
                  </div>
                </div>
              </div>
            </div>

            {/* Mensagens */}
            <div style={styles.messagesContainer} ref={messagesContainerRef}>
              {messages.length === 0 ? (
                <div style={styles.noMessages}>Nenhuma mensagem ainda</div>
              ) : (
                <>
                  {messages.map((msg, index) => renderMessageBubble(msg, index))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input de mensagem */}
            <div style={styles.inputContainer}>
              <textarea
                style={styles.input}
                placeholder="Digite sua mensagem..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={1}
                disabled={sending}
              />
              <button
                style={{
                  ...styles.sendButton,
                  ...(sending || !messageInput.trim() ? styles.sendButtonDisabled : {}),
                }}
                onClick={handleSendMessage}
                disabled={sending || !messageInput.trim()}
              >
                <SendIcon style={{ fontSize: 24 }} />
              </button>
            </div>

            {/* Botão de scroll para baixo */}
            {showScrollButton && (
              <button style={styles.scrollDownButton} onClick={scrollToBottom}>
                <KeyboardArrowDownIcon style={{ fontSize: 32, color: theme.colors.primary }} />
              </button>
            )}
          </>
        )}
      </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    height: 'calc(100vh - 40px)',
    margin: '20px',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  containerMobile: {
    height: 'calc(100vh - 24px)',
    margin: '12px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    fontSize: 18,
    color: '#666',
  },

  // Lista de conversas
  conversationsList: {
    width: '360px',
    borderRight: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    background: '#fafafa',
  },
  conversationsListMobile: {
    width: '100%',
    borderRight: 'none',
  },
  conversationsHeader: {
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
    background: theme.gradient,
  },
  conversationsTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
  },
  conversationsScroll: {
    flex: 1,
    overflowY: 'auto',
  },
  conversationItem: {
    display: 'flex',
    padding: '16px',
    cursor: 'pointer',
    borderBottom: '1px solid #e0e0e0',
    background: '#fff',
    transition: 'background 0.2s',
  },
  conversationItemActive: {
    background: '#e3f2fd',
    borderLeft: `4px solid ${theme.colors.primary}`,
  },
  conversationAvatar: {
    width: 50,
    height: 50,
    borderRadius: '50%',
    background: theme.gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    flexShrink: 0,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  conversationInfo: {
    flex: 1,
    minWidth: 0,
  },
  conversationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  conversationName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  conversationDate: {
    fontSize: 12,
    color: '#999',
  },
  conversationLastMessage: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  lastMessageText: {
    fontSize: 14,
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  unreadBadge: {
    background: theme.colors.primary,
    color: '#fff',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 8,
    flexShrink: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    textAlign: 'center',
  },
  emptyStateText: {
    color: '#999',
    fontSize: 16,
  },

  // Área de chat
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    background: '#fff',
  },
  chatAreaMobile: {
    width: '100%',
  },
  emptyChat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  emptyChatText: {
    color: '#999',
    fontSize: 18,
  },
  chatHeader: {
    padding: '16px 24px',
    borderBottom: '1px solid #e0e0e0',
    background: '#f9f9f9',
  },
  chatHeaderInfo: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  mobileBackButton: {
    border: `1px solid ${theme.colors.tertiary}`,
    background: theme.colors.white,
    color: theme.colors.primary,
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    marginRight: '10px',
    flexShrink: 0,
  },
  chatAvatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: theme.gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  chatHeaderName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  chatHeaderEmail: {
    fontSize: 14,
    color: '#666',
  },

  // Mensagens
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    background: '#f5f5f5',
  },
  noMessages: {
    textAlign: 'center',
    color: '#999',
    padding: 20,
  },
  dayBadge: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20px 0 10px',
  },
  dayBadgeText: {
    background: 'rgba(0, 0, 0, 0.1)',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 12,
    color: '#666',
  },
  messageBubbleContainer: {
    display: 'flex',
    marginBottom: 8,
  },
  messageBubble: {
    maxWidth: '70%',
    padding: '10px 14px',
    borderRadius: 12,
    wordWrap: 'break-word',
  },
  messageBubbleAdmin: {
    background: theme.colors.primary,
    color: '#fff',
    borderBottomRightRadius: 4,
  },
  messageBubbleClient: {
    background: '#e0e0e0',
    color: '#333',
    borderBottomLeftRadius: 4,
  },
  messageText: {
    fontSize: 15,
    lineHeight: 1.4,
    marginBottom: 4,
  },
  messageTime: {
    fontSize: 11,
    textAlign: 'right',
  },

  // Input
  inputContainer: {
    display: 'flex',
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0',
    background: '#fff',
    alignItems: 'flex-end',
    gap: 12,
  },
  input: {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: 24,
    fontSize: 15,
    resize: 'none',
    fontFamily: 'inherit',
    minHeight: 44,
    maxHeight: 120,
    outline: 'none',
  },
  sendButton: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    background: theme.colors.primary,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
    flexShrink: 0,
  },
  sendButtonDisabled: {
    background: '#ccc',
    cursor: 'not-allowed',
  },
  scrollDownButton: {
    position: 'absolute',
    bottom: 90,
    right: 40,
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: 'none',
    background: '#fff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s',
  },
};

export default Support;
